import cn from "classnames";
import { tokenProviderTypesEnum } from "helpers/enums";
import React, { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import QRCode from "react-qr-code";
import {
    finishTfaDisabling,
    finishTfaSetup,
    startTfaDisabling,
    startTfaSetup,
    startTfaSetupAuthenticator,
} from "sources/profile/user";

export default function TwoFaSettings(props) {
    const interval = useRef(null);
    const [provider, setProvider] = useState(props.data.twoFactorProvider || tokenProviderTypesEnum.Phone);
    const [loading, setLoading] = useState(false);
    const [code, setCode] = useState("");
    const [countdown, setCountdown] = useState(0);
    const [authenticatorData, setAuthenticatorData] = useState({ authenticatorKey: "", formattedKey: "" });
    const [codeSended, setCodeSended] = useState(false);

    useEffect(() => {
        return () => clearInterval(interval.current);
    }, []);

    useEffect(() => {
        if (countdown < 0) {
            clearInterval(interval.current);
        }
    }, [countdown]);

    const phoneNumberProps = {
        placeholder: "Введите номер телефона",
        value: props.data.phoneNumber,
        onlyCountries: ["ru", "kz"],
        localization: { ru: "Россия", kz: "Казахстан" },
        disabled: true,
        inputClass: "form-control form-control-sm w-100",
    };
    const renderField = () => {
        switch (provider) {
            case tokenProviderTypesEnum.Phone:
                return <PhoneInput {...phoneNumberProps} />;
            case tokenProviderTypesEnum.Email:
                return (
                    <input
                        className="form-control form-control-sm"
                        type="email"
                        placeholder="Введите email"
                        readOnly
                        value={props.data.email}
                    />
                );
            case tokenProviderTypesEnum.Authenticator:
                return null;
        }
    };

    const startTfa = () => {
        setLoading(true);
        if (provider === tokenProviderTypesEnum.Authenticator) {
            startTfaSetupAuthenticator().then((data) => {
                if (data) {
                    setAuthenticatorData(data);
                }
                setCodeSended(true);
                setLoading(false);
            });
        } else {
            const req = props.data.twoFactorProvider ? startTfaDisabling : startTfaSetup;
            req({ provider }).then(() => {
                setCodeSended(true);
                setLoading(false);
                setCountdown(40);
                interval.current = setInterval(() => {
                    setCountdown((countdown) => countdown - 1);
                }, 1000);
            });
        }
    };

    const finishTfa = (e) => {
        e.preventDefault();
        setLoading(true);
        const req = props.data.twoFactorProvider ? finishTfaDisabling : finishTfaSetup;
        req({ provider, code }).then((res) => {
            if (res) {
                props.refreshProfile();
                props.back();
            }
            setLoading(false);
        });
    };

    const changeProvider = (newProvider) => {
        setProvider(newProvider);
        setCode("");
        setCodeSended(false);
    };

    const renderAcceptCode = () => {
        const sourceText = {
            [tokenProviderTypesEnum.Authenticator]: "Google Authenticator",
            [tokenProviderTypesEnum.Email]: "email",
            [tokenProviderTypesEnum.Phone]: "SMS",
        };
        return (
            <>
                {provider === tokenProviderTypesEnum.Authenticator && !props.data.twoFactorProvider && (
                    <div className="text-center mb-3">
                        {authenticatorData.formattedKey && <QRCode value={authenticatorData.formattedKey} />}
                    </div>
                )}
                <form onSubmit={finishTfa}>
                    <div className="mb-2">
                        <input
                            autoFocus
                            className="form-control form-control-sm"
                            placeholder={`Введите код из ${sourceText[provider]}`}
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                        />
                    </div>
                    <button
                        className={cn("btn btn-sm btn-brand btn-block mb-1", { disabled: code.length === 0 })}
                        type="submit"
                    >
                        Проверить код
                    </button>
                </form>
                {props.data.twoFactorProvider !== tokenProviderTypesEnum.Authenticator &&
                    (countdown > 0 ? (
                        <small className="text-muted">Отправить код заново через {countdown} c.</small>
                    ) : (
                        <small className="link" onClick={startTfa}>
                            {provider === tokenProviderTypesEnum.Authenticator
                                ? "Сгенерировать новый QR код"
                                : "Отправить код заново"}
                        </small>
                    ))}
            </>
        );
    };

    const renderSendCode = () => {
        if (props.data.twoFactorProvider === tokenProviderTypesEnum.Authenticator) {
            return null;
        }
        return (
            <>
                <div className="mb-2">{renderField()}</div>
                <div className="btn btn-sm btn-brand btn-block" onClick={startTfa}>
                    {provider === tokenProviderTypesEnum.Authenticator
                        ? "Сгенерировать QR код"
                        : `Получить код через ${provider === tokenProviderTypesEnum.Email ? "email" : "SMS"}`}
                </div>
            </>
        );
    };

    const useAnotherProviderBtns = () => {
        if (props.data.twoFactorProvider) {
            return null;
        }
        return (
            <>
                <div className="text-center text-muted small py-3">или</div>
                <div className="profile__inline-btns">
                    {provider !== tokenProviderTypesEnum.Phone && (
                        <div
                            className="inline-btn inline-btn--without-icon flex-1 text-center mr-0"
                            onClick={changeProvider.bind(null, tokenProviderTypesEnum.Phone)}
                        >
                            Получить код через SMS
                        </div>
                    )}
                    {provider !== tokenProviderTypesEnum.Email && (
                        <div
                            className="inline-btn inline-btn--without-icon flex-1 text-center mr-0"
                            onClick={changeProvider.bind(null, tokenProviderTypesEnum.Email)}
                        >
                            Получить код через email
                        </div>
                    )}
                    {provider !== tokenProviderTypesEnum.Authenticator && (
                        <div
                            className="inline-btn inline-btn--without-icon flex-1 text-center"
                            onClick={changeProvider.bind(null, tokenProviderTypesEnum.Authenticator)}
                        >
                            Получить код через Google Authenticator
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div className={cn({ loading })}>
            {codeSended || props.data.twoFactorProvider === tokenProviderTypesEnum.Authenticator
                ? renderAcceptCode()
                : renderSendCode()}
            {useAnotherProviderBtns()}
        </div>
    );
}
